import Button from '@/components/Button';

import useReset from '@/hooks/useReset';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Lottie from 'react-lottie-player';
import { useParams, useSearchParams } from 'react-router-dom';
import lottieJson from '@/assets/json/check.json';
import useToggle from '@/hooks/useToggle';
import BackModal from '@/components/BackModal';
import { NotificationBadge } from '@/components/NotificationBadge/NotificationBadge';
import { receivingType } from '@/types/checkout';
import { deviceLocationType } from '@/types/data';
import { usePickupTime } from '@/hooks/usePickupTime';
import PageViewEvent from '@/components/Event/PageViewEvent';

export function OrderSuccessPage() {
  const intl = useIntl();
  const params = useParams();
  const [time, setTime] = useState(20);
  const { goHome } = useReset();
  const backModal = useToggle({ showing: false });
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const shippingType = (searchParams.get('shipping_method') ?? 'pick_up') as receivingType;
  const pickupLocation = (searchParams.get('pickupLocation') as deviceLocationType | null) ?? 'hongdae';
  const offerTotal = searchParams.get('offerTotal') ?? '0';
  const [successPickupTime, setSuccessPickupTime] = useState('');
  const { pickupDate, pickupTime, quickPickupTime, quickPickupDate } = usePickupTime(pickupLocation);

  useEffect(() => {
    // const isQuickPickup = quickPickupDate && Number(offerTotal) >= 100000 && shippingType === 'pick_up';
    // const value = isQuickPickup ? `${quickPickupDate} ${quickPickupTime}` : `${pickupDate} ${pickupTime}`;
    const value = `${pickupDate} ${pickupTime}`;
    setSuccessPickupTime(value);
  }, [pickupDate, pickupTime, quickPickupTime, quickPickupDate]);

  useEffect(() => {
    const id = setInterval(() => {
      setTime((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (time === 0) {
      goHome();
    }
  }, [time]);

  return (
    <PageViewEvent eventName="Complete_view">
      <div className="w-screen h-screen flex flex-col items-center justify-center gap-8 pt-0">
        <div className="flex flex-col justify-center items-center">
          <div className="flex flex-col gap-4 items-center w-full">
            <Lottie animationData={lottieJson} loop={false} play speed={0.5} style={{ width: 120, height: 120 }} />
            <p className="text-[34px] font-extrabold text-center pb-10">
              <FormattedMessage id="please_head_to_counter" defaultMessage="Please head to counter" /> <br />
              <FormattedMessage id="and_make_a_payment" defaultMessage="and make a payment." />
            </p>
          </div>
          <div className="w-[436px] flex flex-col items-center">
            <div className="w-[730px] rounded-[10px] bg-[#f5f5f5] px-10 flex flex-col justify-between gap-4 py-6 text-[20px] leading-7 font-medium">
              <p className="flex justify-between text-[#4d4d4d]">
                <span>
                  <FormattedMessage id="order_cart_id" defaultMessage="Order ID" />
                </span>
                <div style={{ position: 'relative' }} className="text-[30px] font-extrabold text-[#ff6060]">
                  <NotificationBadge
                    position={{ top: -2, left: 114 }}
                    message={intl.formatMessage({ id: 'show_this_to_the_staff', defaultMessage: 'Show this to the staff.' })}
                  />
                  {params.id}
                </div>
              </p>

              {shippingType === 'pick_up' && (
                <div className="flex justify-between text-[#4d4d4d]">
                  <div>
                    <FormattedMessage id="pick_up_schedule" defaultMessage="Pick-up schedule" />
                  </div>
                  <span className="text-[24px] font-bold text-[#ff6060]">{`${successPickupTime} PM`}</span>
                </div>
              )}

              <div className="flex flex-col">
                <p className="flex justify-between text-[#4d4d4d]">
                  <span>
                    <FormattedMessage id="order_section1_input4_label" defaultMessage="E-mail" />
                  </span>
                  <span>{email}</span>
                </p>
              </div>
            </div>
          </div>
          <span className="text-[16px] text-[#4d4d4d] font-medium text-right mt-6 mb-[62px]">
            <FormattedMessage
              id={shippingType === 'pick_up' ? 'order_success_email_desc' : 'order_success_email_shipping_desc'}
            />
          </span>
        </div>

        <div className="flex gap-5">
          <Button className="bg-[#f2f2f2]" textColor="black" onClick={() => setTime(20)}>
            <FormattedMessage id="order_success_stay_screen" defaultMessage="Stay on screen (20sec)" />
          </Button>
          <Button data-primary onClick={() => backModal.open()}>
            <FormattedMessage id="order_button_main" defaultMessage="Go to main" />
          </Button>
        </div>

        <span>
          {intl.formatMessage({ id: 'order_success_order-info3', defaultMessage: 'After' })}{' '}
          <span className="text-[16px] font-bold text-black mt-4">{time} </span>
          <FormattedMessage
            id="order_success_order-info4"
            defaultMessage="seconds, this screen will move back to the main menu."
          />
        </span>

        {backModal.isShowing && <BackModal onClick={goHome} onClose={() => backModal.close()} />}
      </div>
    </PageViewEvent>
  );
}
